// Seção "Avaliação composta" dentro do detalhe de uma disciplina.
//
// Permite ao professor:
//   - Definir nota mínima de aprovação e limite pra recuperação
//   - Criar items (Prova ou Atividade) com pesos somando 10
//   - Para item kind="prova": vincular a uma prova agendada já existente
//     (a nota vem automática do attempt do aluno)
//   - Para item kind="atividade": lançar nota manual por aluno
//   - Visualizar a tabela aluno × item × nota com status final calculado

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Edit3,
  Flag,
  Loader2,
  Plus,
  Save,
  Trash2,
  Users,
  XCircle
} from "lucide-react";
import {
  api,
  ApiError,
  type DisciplinaEvaluationItem,
  type DisciplinaEvaluationSnapshot,
  type ExamRow
} from "../api";
import { Switch } from "@app/ui/components/switch";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

export function AvaliacaoSection({ disciplinaId }: { disciplinaId: string }) {
  const [snap, setSnap] = useState<DisciplinaEvaluationSnapshot | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [allExams, setAllExams] = useState<ExamRow[]>([]);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await api.getDisciplinaEvaluation(disciplinaId);
      setSnap(r);
      setPhase("ready");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        window.location.href = "/auth/";
        return;
      }
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
    void api
      .listExams()
      .then((r) => setAllExams(r.exams))
      .catch(() => setAllExams([]));
  }, [disciplinaId]);

  if (phase === "loading") {
    return (
      <section class="rounded-md border border-border bg-card/40 p-4">
        <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Flag className="size-3.5" /> Avaliação composta
        </h3>
        <p class="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Carregando…
        </p>
      </section>
    );
  }
  if (phase === "error" || !snap) {
    return (
      <section class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <AlertCircle className="mr-1 inline size-3.5 text-destructive" />
        <span class="text-xs text-destructive">{errMsg}</span>
      </section>
    );
  }

  const somaPesos = snap.items.reduce((a, b) => a + b.weight, 0);
  const somaOk = Math.abs(somaPesos - 10) < 0.01;

  return (
    <section class="space-y-3 rounded-md border border-border bg-card/40 p-4">
      <header class="flex flex-wrap items-center justify-between gap-2">
        <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
          <Flag className="size-3.5" /> Avaliação composta da disciplina
        </h3>
        <button
          type="button"
          onClick={() => setCreating(true)}
          class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] font-bold text-primary hover:bg-primary/20"
        >
          <Plus className="size-3" /> Novo item
        </button>
      </header>

      <ThresholdsBlock snap={snap} onSaved={(s) => setSnap(s)} />

      <div class="rounded-md border border-border bg-background/30 px-3 py-2 text-[11px]">
        Soma dos pesos:{" "}
        <strong
          class={`tabular-nums ${
            somaOk
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-amber-600 dark:text-amber-400"
          }`}
        >
          {somaPesos.toFixed(2)}
        </strong>{" "}
        / <span class="text-muted-foreground">10.00</span>
        {!somaOk && (
          <span class="ml-2 text-[10px] text-amber-700 dark:text-amber-400">
            (precisa fechar em 10 pra computar nota final corretamente)
          </span>
        )}
      </div>

      {snap.items.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-background/30 px-4 py-6 text-center text-xs text-muted-foreground">
          Nenhum item ainda. Crie itens (Prova ou Atividade) com pesos somando 10.
        </div>
      ) : (
        <ul class="space-y-2">
          {snap.items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              snap={snap}
              allExams={allExams}
              onSnap={(s) => setSnap(s)}
            />
          ))}
        </ul>
      )}

      {snap.items.length > 0 && snap.students.length > 0 && (
        <StudentsResultsTable snap={snap} />
      )}

      {creating && (
        <CreateItemModal
          disciplinaId={disciplinaId}
          allExams={allExams}
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setSnap(s);
            setCreating(false);
          }}
        />
      )}
    </section>
  );
}

// =====================
// Thresholds (passing / recovery)
// =====================

function ThresholdsBlock({
  snap,
  onSaved
}: {
  snap: DisciplinaEvaluationSnapshot;
  onSaved: (s: DisciplinaEvaluationSnapshot) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [passing, setPassing] = useState(snap.disciplina.passingGrade);
  const [recovery, setRecovery] = useState(snap.disciplina.recoveryGrade);
  const [recoveryEnabled, setRecoveryEnabled] = useState(
    snap.disciplina.recoveryEnabled
  );
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const r = await api.patchDisciplinaEvaluation(snap.disciplina.id, {
        passingGrade: passing,
        recoveryGrade: recovery,
        recoveryEnabled
      });
      onSaved(r);
      setEditing(false);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[11px]">
      {editing ? (
        <>
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-1">
              <span class="font-semibold uppercase tracking-wider text-muted-foreground">
                Aprovação
              </span>
              <input
                type="number"
                value={passing}
                step="0.5"
                min="0"
                max="10"
                onInput={(e) =>
                  setPassing(Number((e.target as HTMLInputElement).value))
                }
                class="w-16 rounded-md border border-border bg-background px-2 py-0.5 text-right text-xs tabular-nums outline-none focus:border-primary"
              />
            </label>
            <label class="flex items-center gap-1">
              <span class="font-semibold uppercase tracking-wider text-muted-foreground">
                Recuperação a partir de
              </span>
              <input
                type="number"
                value={recovery}
                step="0.5"
                min="0"
                max="10"
                disabled={!recoveryEnabled}
                onInput={(e) =>
                  setRecovery(Number((e.target as HTMLInputElement).value))
                }
                class="w-16 rounded-md border border-border bg-background px-2 py-0.5 text-right text-xs tabular-nums outline-none focus:border-primary disabled:opacity-50"
              />
            </label>
            <label class="flex items-center gap-1.5">
              <Switch
                checked={recoveryEnabled}
                onCheckedChange={(v) => setRecoveryEnabled(v)}
              />
              <span class="font-semibold">Recuperação ativa</span>
            </label>
          </div>
          <div class="flex gap-1.5">
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              class="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              class="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-bold text-white hover:brightness-110"
            >
              {saving ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Save className="size-3" />
              )}
              Salvar
            </button>
          </div>
        </>
      ) : (
        <>
          <div class="flex flex-wrap items-center gap-3">
            <span>
              <strong class="tabular-nums">{snap.disciplina.passingGrade.toFixed(2)}</strong>{" "}
              <span class="text-muted-foreground">para aprovação</span>
            </span>
            {snap.disciplina.recoveryEnabled ? (
              <span>
                · entre{" "}
                <strong class="tabular-nums">{snap.disciplina.recoveryGrade.toFixed(2)}</strong>{" "}
                e{" "}
                <strong class="tabular-nums">
                  {snap.disciplina.passingGrade.toFixed(2)}
                </strong>{" "}
                <span class="text-muted-foreground">vai pra recuperação</span>
              </span>
            ) : (
              <span class="text-muted-foreground">· sem recuperação automática</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
          >
            <Edit3 className="size-3" /> Editar
          </button>
        </>
      )}
    </div>
  );
}

// =====================
// Linha de item (com peso editável + delete + vínculo a prova)
// =====================

function ItemRow({
  item,
  snap,
  allExams,
  onSnap
}: {
  item: DisciplinaEvaluationItem;
  snap: DisciplinaEvaluationSnapshot;
  allExams: ExamRow[];
  onSnap: (s: DisciplinaEvaluationSnapshot) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [weight, setWeight] = useState(item.weight);
  const [savingW, setSavingW] = useState(false);

  async function saveWeight() {
    if (weight === item.weight) return;
    setSavingW(true);
    try {
      const r = await api.patchEvaluationItem(item.id, { weight });
      onSnap(r);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSavingW(false);
    }
  }

  async function removeItem() {
    if (!confirm(`Remover item "${item.title}"? Notas lançadas serão perdidas.`)) return;
    try {
      const r = await api.deleteEvaluationItem(item.id);
      onSnap(r);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  async function linkAssessment(assessmentId: string | null) {
    try {
      const r = await api.patchEvaluationItem(item.id, { assessmentId });
      onSnap(r);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  const tone =
    item.kind === "prova"
      ? "border-primary/30 bg-primary/5"
      : "border-violet-500/30 bg-violet-500/5";

  return (
    <li class={`overflow-hidden rounded-md border ${tone}`}>
      <div class="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          class="grid size-6 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          title={expanded ? "Recolher" : "Expandir"}
        >
          {expanded ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
        </button>
        <span
          class={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            item.kind === "prova"
              ? "bg-primary/15 text-primary"
              : "bg-violet-500/15 text-violet-700 dark:text-violet-300"
          }`}
        >
          {item.kind === "prova" ? "Prova" : "Atividade"}
        </span>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold">{item.title}</div>
          {item.assessmentTitle && (
            <div class="truncate text-[10px] text-primary">
              Vinculada à prova: {item.assessmentTitle}
            </div>
          )}
        </div>
        <label class="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Peso
          <input
            type="number"
            value={weight}
            step="0.1"
            min="0"
            max="10"
            onInput={(e) =>
              setWeight(Number((e.target as HTMLInputElement).value))
            }
            onBlur={saveWeight}
            disabled={savingW}
            class="w-16 rounded-md border border-border bg-background px-2 py-1 text-right text-xs tabular-nums outline-none focus:border-primary"
          />
        </label>
        <button
          type="button"
          onClick={removeItem}
          title="Remover item"
          class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      {expanded && (
        <div class="border-t border-border bg-background/40 px-4 py-3 text-xs">
          {item.description && (
            <p class="mb-2 text-muted-foreground">{item.description}</p>
          )}
          {item.kind === "prova" ? (
            <div class="space-y-2">
              <label class="block">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Vincular a uma prova agendada
                </span>
                <select
                  value={item.assessmentId ?? ""}
                  onChange={(e) =>
                    linkAssessment(
                      (e.target as HTMLSelectElement).value || null
                    )
                  }
                  class="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-primary"
                >
                  <option value="">— Sem vínculo (notas manuais não suportadas em Prova) —</option>
                  {allExams.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title}{" "}
                      {e.startsAt
                        ? `(${new Date(e.startsAt).toLocaleDateString("pt-BR")})`
                        : ""}
                    </option>
                  ))}
                </select>
                <p class="mt-1 text-[10px] text-muted-foreground">
                  Quando vinculada, a nota de cada aluno vem direto do resultado
                  da prova agendada.
                </p>
              </label>
            </div>
          ) : (
            <ManualGradesEditor item={item} snap={snap} onSnap={onSnap} />
          )}
        </div>
      )}
    </li>
  );
}

// =====================
// Editor de notas manuais (atividade)
// =====================

function ManualGradesEditor({
  item,
  snap,
  onSnap
}: {
  item: DisciplinaEvaluationItem;
  snap: DisciplinaEvaluationSnapshot;
  onSnap: (s: DisciplinaEvaluationSnapshot) => void;
}) {
  return (
    <div class="space-y-1.5">
      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Notas manuais ({snap.students.length} {snap.students.length === 1 ? "aluno" : "alunos"})
      </div>
      <ul class="space-y-1">
        {snap.students.map((s) => (
          <GradeRow
            key={s.userId}
            item={item}
            student={{
              userId: s.userId,
              name: s.name,
              currentScore: s.grades[item.id] ?? null
            }}
            onSnap={onSnap}
          />
        ))}
      </ul>
    </div>
  );
}

function GradeRow({
  item,
  student,
  onSnap
}: {
  item: DisciplinaEvaluationItem;
  student: { userId: string; name: string; currentScore: number | null };
  onSnap: (s: DisciplinaEvaluationSnapshot) => void;
}) {
  const [editValue, setEditValue] = useState<string>(
    student.currentScore != null ? String(student.currentScore) : ""
  );
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = Number(editValue);
    if (editValue === "" || !Number.isFinite(v)) return;
    setSaving(true);
    try {
      const r = await api.setEvaluationGrade(item.id, student.userId, {
        score: v
      });
      onSnap(r);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  async function clearGrade() {
    setSaving(true);
    try {
      const r = await api.deleteEvaluationGrade(item.id, student.userId);
      onSnap(r);
      setEditValue("");
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <li class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1">
      <span class="min-w-0 flex-1 truncate text-xs">{student.name}</span>
      <input
        type="number"
        value={editValue}
        step="0.1"
        min="0"
        max="10"
        placeholder="—"
        onInput={(e) => setEditValue((e.target as HTMLInputElement).value)}
        onBlur={save}
        disabled={saving}
        class="w-16 rounded-md border border-border bg-background px-2 py-0.5 text-right text-xs tabular-nums outline-none focus:border-primary"
      />
      {student.currentScore != null && (
        <button
          type="button"
          onClick={clearGrade}
          disabled={saving}
          title="Limpar nota"
          class="grid size-6 shrink-0 place-items-center rounded text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
        >
          <XCircle className="size-3.5" />
        </button>
      )}
    </li>
  );
}

// =====================
// Tabela de resultados
// =====================

function StudentsResultsTable({ snap }: { snap: DisciplinaEvaluationSnapshot }) {
  return (
    <div class="space-y-2">
      <h4 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Users className="size-3.5" /> Notas finais
      </h4>
      <div class="overflow-x-auto rounded-md border border-border">
        <table class="w-full text-xs">
          <thead class="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th class="px-3 py-2 text-left">Aluno</th>
              {snap.items.map((it) => (
                <th key={it.id} class="px-2 py-2 text-right">
                  <div class="font-semibold normal-case">{it.title}</div>
                  <div class="text-[9px] font-normal opacity-70">
                    peso {it.weight.toFixed(2)}
                  </div>
                </th>
              ))}
              <th class="px-3 py-2 text-right">Final</th>
              <th class="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            {snap.students.map((s) => (
              <tr key={s.userId} class="hover:bg-accent/40">
                <td class="px-3 py-2">
                  <div class="font-semibold">{s.name}</div>
                  <div class="text-[10px] text-muted-foreground">{s.email}</div>
                </td>
                {snap.items.map((it) => {
                  const g = s.grades[it.id];
                  return (
                    <td
                      key={it.id}
                      class="px-2 py-2 text-right font-mono tabular-nums"
                    >
                      {g == null ? (
                        <span class="text-muted-foreground">—</span>
                      ) : (
                        g.toFixed(2)
                      )}
                    </td>
                  );
                })}
                <td class="px-3 py-2 text-right text-sm font-black tabular-nums">
                  {s.finalScore.toFixed(2)}
                </td>
                <td class="px-3 py-2">
                  <StatusBadge status={s.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "aprovado" | "recuperacao" | "reprovado" | "incompleto" }) {
  const map = {
    aprovado: {
      cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30",
      label: "Aprovado",
      Icon: CheckCircle2
    },
    recuperacao: {
      cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30",
      label: "Recuperação",
      Icon: ClipboardList
    },
    reprovado: {
      cls: "bg-red-500/15 text-red-600 dark:text-red-400 ring-red-500/30",
      label: "Reprovado",
      Icon: XCircle
    },
    incompleto: {
      cls: "bg-muted text-muted-foreground ring-border",
      label: "Incompleto",
      Icon: ClipboardList
    }
  } as const;
  const m = map[status];
  return (
    <span
      class={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${m.cls}`}
    >
      <m.Icon className="size-3" />
      {m.label}
    </span>
  );
}

// =====================
// Modal de criação de item
// =====================

function CreateItemModal({
  disciplinaId,
  allExams,
  onClose,
  onCreated
}: {
  disciplinaId: string;
  allExams: ExamRow[];
  onClose: () => void;
  onCreated: (s: DisciplinaEvaluationSnapshot) => void;
}) {
  const [kind, setKind] = useState<"prova" | "atividade">("prova");
  const [title, setTitle] = useState("");
  const [weight, setWeight] = useState<number>(2);
  const [description, setDescription] = useState("");
  const [assessmentId, setAssessmentId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  async function go() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const r = await api.createEvaluationItem(disciplinaId, {
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        weight,
        assessmentId: kind === "prova" && assessmentId ? assessmentId : null
      });
      onCreated(r);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      // Modal admin de create/edit não fecha por click no backdrop —
      // [[feedback-modal-no-click-outside-close]]
    >
      <div class="w-full max-w-md space-y-4 rounded-md border border-border bg-card p-5 shadow-2xl">
        <h3 class="flex items-center gap-2 text-base font-bold">
          <Plus className="size-4 text-primary" />
          Novo item de avaliação
        </h3>

        <div class="space-y-2">
          <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Tipo
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={() => setKind("prova")}
              class={`flex-1 rounded-md border px-3 py-2 text-left ${
                kind === "prova"
                  ? "border-primary/60 bg-primary/10"
                  : "border-border bg-background hover:bg-accent"
              }`}
            >
              <div class="text-sm font-bold">Prova</div>
              <div class="text-[10px] text-muted-foreground">
                Vinculada a uma prova agendada (nota automática)
              </div>
            </button>
            <button
              type="button"
              onClick={() => setKind("atividade")}
              class={`flex-1 rounded-md border px-3 py-2 text-left ${
                kind === "atividade"
                  ? "border-violet-500/60 bg-violet-500/10"
                  : "border-border bg-background hover:bg-accent"
              }`}
            >
              <div class="text-sm font-bold">Atividade</div>
              <div class="text-[10px] text-muted-foreground">
                Nota lançada manualmente por aluno
              </div>
            </button>
          </div>
        </div>

        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Título *
          </span>
          <input
            type="text"
            value={title}
            placeholder={kind === "prova" ? "Ex: Prova bimestral 1" : "Ex: Seminário em grupo"}
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            autoFocus
          />
        </label>

        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Peso (na soma total que deve fechar em 10)
          </span>
          <input
            type="number"
            value={weight}
            step="0.5"
            min="0"
            max="10"
            onInput={(e) =>
              setWeight(Number((e.target as HTMLInputElement).value))
            }
            class="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm tabular-nums outline-none focus:border-primary"
          />
        </label>

        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Descrição (opcional)
          </span>
          <textarea
            value={description}
            onInput={(e) =>
              setDescription((e.target as HTMLTextAreaElement).value)
            }
            rows={2}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>

        {kind === "prova" && (
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Vincular a uma prova agendada (opcional, dá pra vincular depois)
            </span>
            <select
              value={assessmentId}
              onChange={(e) =>
                setAssessmentId((e.target as HTMLSelectElement).value)
              }
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              <option value="">— Sem vínculo —</option>
              {allExams.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </label>
        )}

        <div class="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={go}
            disabled={saving || !title.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Plus width="0.9em" height="0.9em" />
            )}
            Criar
          </button>
        </div>
      </div>
    </div>
  );
}
